import React from "react";
import { Send, Phone, Mail, Instagram, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { useContactForm } from "../hooks/useContactForm";

const Contact = () => {
  const { form, update, submit, status, loading } = useContactForm();

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#F4F1E8] py-24 md:py-32 border-t-2 border-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-mono-tag text-xs text-[#0A0A0A] mb-4">// CONTACTO</p>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-8 text-[#0A0A0A]">
              ASEGURA TU
              <span className="text-[#F5E60F]"> PROYECTO.</span>
            </h2>
            <p className="text-[#3A3A3A] text-base md:text-lg mb-10 leading-relaxed">
              Cuéntanos sobre tu obra y te respondemos con una propuesta técnica y
              económica en menos de 24 horas hábiles.
            </p>

            <div className="space-y-0 border-t border-[#0A0A0A]">
              <a
                href="tel:+523223842690"
                data-testid="contact-phone-direct"
                className="flex items-start gap-4 py-5 border-b border-[#0A0A0A] group"
              >
                <Phone className="text-[#F5E60F] mt-1" size={18} />
                <div>
                  <p className="font-mono-tag text-[10px] text-[#9A9A9A]">TELÉFONO</p>
                  <p className="font-display text-xl text-[#0A0A0A] group-hover:text-[#F5E60F] transition-colors">
                    322 384 2690
                  </p>
                </div>
              </a>
              <a
                href="mailto:ventas@alambradosaloma.com"
                data-testid="contact-email-direct"
                className="flex items-start gap-4 py-5 border-b border-[#0A0A0A] group"
              >
                <Mail className="text-[#F5E60F] mt-1" size={18} />
                <div>
                  <p className="font-mono-tag text-[10px] text-[#9A9A9A]">EMAIL</p>
                  <p className="font-display text-lg text-[#0A0A0A] group-hover:text-[#F5E60F] transition-colors break-all">
                    ventas@alambradosaloma.com
                  </p>
                </div>
              </a>
              <a
                href="https://www.instagram.com/alambradosaloma?igsh=NzRsc3RzeXVqc2pp"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="flex items-start gap-4 py-5 border-b border-[#0A0A0A] group"
              >
                <Instagram className="text-[#F5E60F] mt-1" size={18} />
                <div>
                  <p className="font-mono-tag text-[10px] text-[#9A9A9A]">INSTAGRAM</p>
                  <p className="font-display text-xl text-[#0A0A0A] group-hover:text-[#F5E60F] transition-colors">
                    @alambradosaloma
                  </p>
                </div>
              </a>
              <div className="flex items-start gap-4 py-5">
                <MapPin className="text-[#F5E60F] mt-1" size={18} />
                <div>
                  <p className="font-mono-tag text-[10px] text-[#9A9A9A]">COBERTURA</p>
                  <p className="font-display text-xl text-[#0A0A0A]">Cobertura Nacional</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border-2 border-[#0A0A0A] p-6 md:p-10 bg-white"
            >
              <p className="font-mono-tag text-xs text-[#F5E60F] mb-6">// FORMULARIO DE COTIZACIÓN</p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-mono-tag text-[10px] text-[#5A5A5A] block mb-2">NOMBRE *</label>
                  <input
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Tu nombre completo"
                    className="w-full bg-transparent border-b-2 border-[#0A0A0A] focus-yellow text-[#0A0A0A] py-3 px-0 placeholder:text-[#9A9A9A] transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono-tag text-[10px] text-[#5A5A5A] block mb-2">EMAIL *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="tu@correo.com"
                    className="w-full bg-transparent border-b-2 border-[#0A0A0A] focus-yellow text-[#0A0A0A] py-3 px-0 placeholder:text-[#9A9A9A] transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono-tag text-[10px] text-[#5A5A5A] block mb-2">TELÉFONO *</label>
                  <input
                    data-testid="contact-input-phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="33 1234 5678"
                    className="w-full bg-transparent border-b-2 border-[#0A0A0A] focus-yellow text-[#0A0A0A] py-3 px-0 placeholder:text-[#9A9A9A] transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono-tag text-[10px] text-[#5A5A5A] block mb-2">PRODUCTO DE INTERÉS</label>
                  <input
                    data-testid="contact-input-service"
                    value={form.service}
                    onChange={update("service")}
                    placeholder="Ej. Malla ciclónica"
                    className="w-full bg-transparent border-b-2 border-[#0A0A0A] focus-yellow text-[#0A0A0A] py-3 px-0 placeholder:text-[#9A9A9A] transition-colors"
                  />
                </div>
              </div>

              <div className="mb-8">
                <label className="font-mono-tag text-[10px] text-[#5A5A5A] block mb-2">MENSAJE *</label>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Describe brevemente tu proyecto, ubicación, metros lineales y plazos..."
                  className="w-full bg-transparent border-b-2 border-[#0A0A0A] focus-yellow text-[#0A0A0A] py-3 px-0 placeholder:text-[#9A9A9A] transition-colors resize-none"
                />
              </div>

              {status.type === "ok" && (
                <div
                  data-testid="contact-success"
                  className="mb-6 border-2 border-[#F5E60F] bg-[#FFF44F]/30 p-4 flex items-start gap-3"
                >
                  <CheckCircle2 size={20} className="text-[#0A0A0A] mt-0.5" />
                  <p className="text-sm text-[#0A0A0A] font-medium">{status.text}</p>
                </div>
              )}
              {status.type === "error" && (
                <div
                  data-testid="contact-error"
                  className="mb-6 border-2 border-red-600 bg-red-50 p-4 flex items-start gap-3"
                >
                  <AlertCircle size={20} className="text-red-600 mt-0.5" />
                  <p className="text-sm text-[#0A0A0A]">{status.text}</p>
                </div>
              )}

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="w-full md:w-auto bg-[#0A0A0A] text-[#FFF44F] px-10 py-5 font-bold uppercase tracking-widest text-sm hover:bg-[#FFF44F] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-[#0A0A0A] inline-flex items-center justify-center gap-3"
              >
                {loading ? "Enviando..." : "Enviar Solicitud"}
                {!loading && <Send size={16} />}
              </button>
              <p className="mt-4 text-xs text-[#5A5A5A]">
                Al enviar aceptas que te contactemos por email o teléfono respecto a tu proyecto.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
